import { useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMe } from '../lib/hooks';
import { DEV_AUTH } from '../lib/api';
import { AppHeader } from '../components/app-header';
import { ConsoleSidebar } from '../components/console-sidebar';
import { LocalBackendError } from '../components/local-backend-error';

const COLLAPSE_KEY = 'cumpli:sidebar-collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Cumplimiento console frame — shared top bar + a collapsible left sidebar
 * (persistent rail on desktop, off-canvas drawer on mobile). Nav items are
 * hidden per role for UX only; the server enforces RBAC.
 */
export default function AppLayout() {
  const { t } = useTranslation();
  const { data: me, isLoading, isError } = useMe();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  if (isLoading) {
    return <div className="grid min-h-screen place-items-center bg-canvas text-ink-faint">{t('common.loading')}</div>;
  }
  if (isError || !me) {
    // Local dev-auth demo never routes to the login portal. If the local API is
    // unreachable, say so inline instead of bouncing to the Cognito sign-in.
    if (DEV_AUTH) {
      return <LocalBackendError />;
    }
    return <Navigate to="/login" replace state={{ sessionExpired: true }} />;
  }

  function toggleCollapse() {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(COLLAPSE_KEY, String(next));
      } catch {
        /* storage unavailable — session-only */
      }
      return next;
    });
  }

  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <AppHeader showBrand onMenu={() => setMobileOpen(true)} />

      <div className="flex flex-1">
        <ConsoleSidebar
          roles={me.roles}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
          mobileOpen={mobileOpen}
          onCloseMobile={() => setMobileOpen(false)}
        />

        <main className="min-w-0 flex-1 overflow-x-clip px-4 py-8 sm:px-6">
          <div className="mx-auto w-full max-w-6xl">
            <Outlet context={{ me }} />
          </div>
        </main>
      </div>
    </div>
  );
}
